import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { ShieldCheck, Loader2, CheckCircle2, Sparkles, TrendingDown, Building2 } from 'lucide-react';
import {
  supabase,
  getErrorMessage,
  getCurrentOrganisationId,
  isMockMode,
  isSupabaseMisconfigured,
  supabaseConfigErrorMessage,
} from '../services/supabaseClient';
import { useAuth } from '../App';
import {
  clearPendingFleetTrialMetadata,
  FLEETMODE_CONSENT_TEXT,
  getFleetTrialParams,
  initializeFleetTrial,
  isFleetTrialSignup,
  persistPendingFleetTrialMetadata,
  readPendingFleetTrialMetadata,
  type FleetTrialSignupMetadata,
} from '../services/fleetTrial';
import { getMandatorySignupFieldsError, getMissingMandatorySignupFields } from '../utils/signupValidation';

const AUTH_BRAND_NAME = 'PCNGuard Fleet';
const AUTH_LOGO_SRC = '/logo.svg';

const ALLOWED_POST_AUTH_PREFIXES = [
  '/dashboard',
  '/tasks',
  '/tickets',
  '/vehicles',
  '/hires',
  '/drivers',
  '/driver-invoices',
  '/settings',
  '/reports',
  '/pricing',
  '/billing',
  '/checkout',
  '/stripe-callback',
] as const;

const sanitizePostAuthPath = (candidate: string | null, fallback: string): string => {
  if (!candidate) return fallback;

  const trimmed = candidate.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return fallback;

  let parsed: URL;
  try {
    parsed = new URL(trimmed, 'http://pcnguard.local');
  } catch {
    return fallback;
  }

  if (parsed.origin !== 'http://pcnguard.local') return fallback;

  const normalized = `${parsed.pathname}${parsed.search}`;
  const isRootPath = normalized === '/' || normalized.startsWith('/?');
  const isAllowedPath = ALLOWED_POST_AUTH_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`) || normalized.startsWith(`${prefix}?`),
  );

  return isRootPath || isAllowedPath ? normalized : fallback;
};

const INPUT_CLASS =
  'w-full h-11 px-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-200 focus:border-emerald-500 outline-none transition-all bg-white text-slate-900 placeholder:text-slate-400';
const LABEL_CLASS = 'block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-600 mb-1.5';
const PRIMARY_BUTTON_CLASS =
  'w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl transition-colors flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed';
const FIELD_GRID_CLASS = 'grid gap-4 sm:grid-cols-2';
const REQUIRED_DETAILS_NOTE = 'All fields are required so your workspace, primary contact, and setup support details are correct from day one.';

const shellStyle: React.CSSProperties = {
  fontFamily: '"Avenir Next", "Segoe UI", Tahoma, sans-serif',
};

const AuthShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen relative overflow-hidden bg-slate-950" style={shellStyle}>
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute -top-28 -left-24 h-72 w-72 rounded-full bg-emerald-400/20 blur-3xl" />
      <div className="absolute top-1/3 -right-24 h-80 w-80 rounded-full bg-cyan-400/15 blur-3xl" />
      <div className="absolute -bottom-24 left-1/3 h-72 w-72 rounded-full bg-slate-500/20 blur-3xl" />
    </div>
    <div className="relative z-10 min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8">{children}</div>
  </div>
);

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, loading: authLoading, profile } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [consentToContact, setConsentToContact] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [googleSessionGraceExpired, setGoogleSessionGraceExpired] = useState(false);
  const [existingWorkspaceDetailsOpen, setExistingWorkspaceDetailsOpen] = useState(false);

  const params = new URLSearchParams(location.search);
  const fleetTrialParams = getFleetTrialParams(location.search);
  const pendingFleetTrialMetadata = readPendingFleetTrialMetadata();
  const isFleetTrial = isFleetTrialSignup(fleetTrialParams);
  const isFleetTrialFlow = isFleetTrial || Boolean(pendingFleetTrialMetadata && isFleetTrialSignup(pendingFleetTrialMetadata));
  const onboarding = params.get('onboarding') === '1';
  const googleOnboarding = params.get('google_onboarding') === '1';
  const next = params.get('next');
  const sessionUserId = session?.user?.id ?? null;
  const sessionEmail = session?.user?.email || profile.email || null;
  const hasSessionUser = Boolean(sessionUserId);
  const fallbackPostAuthPath = onboarding ? '/?onboarding=1' : '/';
  const postAuthPath = sanitizePostAuthPath(next, fallbackPostAuthPath);
  const shouldShowGoogleCompanyStep = googleOnboarding && hasSessionUser;
  const shouldShowGooglePendingState = googleOnboarding && !hasSessionUser && (authLoading || !googleSessionGraceExpired);

  useEffect(() => {
    const pending = readPendingFleetTrialMetadata();
    if (!pending) return;
    setFirstName(String(pending.first_name || '').trim());
    setLastName(String(pending.last_name || '').trim());
    setCompanyName(String(pending.company_name || '').trim());
    setPhone(String(pending.phone || '').trim());
    setConsentToContact(pending.consent_to_contact === true);
  }, []);

  useEffect(() => {
    if (!session?.user) return;
    const meta = session.user.user_metadata || {};
    const profileNameParts = String(profile.fullName || '').trim().split(/\s+/).filter(Boolean);
    if (!firstName) setFirstName(String(meta.first_name || profileNameParts[0] || '').trim());
    if (!lastName) setLastName(String(meta.last_name || profileNameParts.slice(1).join(' ') || '').trim());
    if (!companyName) setCompanyName(String(meta.company_name || '').trim());
    if (!phone) setPhone(String(meta.phone || '').trim());
  }, [companyName, firstName, lastName, phone, profile.fullName, session?.user]);

  useEffect(() => {
    if (!hasSessionUser || !isFleetTrial || googleOnboarding) return;
    let cancelled = false;

    const loadExistingOrganisation = async () => {
      try {
        const organisationId = await getCurrentOrganisationId();
        if (!organisationId || cancelled) return;

        const { data, error } = await supabase
          .from('organisations')
          .select('name, phone')
          .eq('id', organisationId)
          .maybeSingle();

        if (error || cancelled || !data) return;
        if (!companyName && data.name) setCompanyName(String(data.name).trim());
        if (!phone && data.phone) setPhone(String(data.phone).trim());
      } catch {
        // Existing-workspace signup still works if these optional defaults cannot be loaded.
      }
    };

    loadExistingOrganisation();

    return () => {
      cancelled = true;
    };
  }, [companyName, googleOnboarding, hasSessionUser, isFleetTrial, phone]);

  useEffect(() => {
    if (!shouldShowGoogleCompanyStep) return;
    const existingCompany = String(session?.user?.user_metadata?.company_name || '').trim();
    if (existingCompany) setCompanyName(existingCompany);
  }, [shouldShowGoogleCompanyStep, session?.user?.user_metadata?.company_name]);

  const buildFleetTrialMetadata = (): FleetTrialSignupMetadata => {
    const cleanedFirstName = firstName.trim();
    const cleanedLastName = lastName.trim();
    const cleanedCompanyName = companyName.trim();
    const cleanedPhone = phone.trim();
    return {
      ...fleetTrialParams,
      first_name: cleanedFirstName,
      last_name: cleanedLastName,
      full_name: [cleanedFirstName, cleanedLastName].filter(Boolean).join(' '),
      company_name: cleanedCompanyName,
      phone: cleanedPhone,
      consent_to_contact: consentToContact,
      consent_text: FLEETMODE_CONSENT_TEXT,
      consent_captured_at: new Date().toISOString(),
    };
  };

  const validateMandatorySignupFields = (options: { includeCredentials?: boolean } = {}) => {
    const missingFields = getMissingMandatorySignupFields(
      { firstName, lastName, companyName, phone, email, password, confirmPassword },
      options,
    );
    const message = getMandatorySignupFieldsError(missingFields);
    if (message) {
      setError(message);
      return false;
    }
    return true;
  };

  const validateFleetTrialConsent = () => {
    if (!isFleetTrialFlow) return true;
    if (!consentToContact) {
      setError('Please agree to be contacted about your FleetMode trial.');
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (!googleOnboarding || hasSessionUser || authLoading) {
      setGoogleSessionGraceExpired(false);
      return;
    }

    const timer = window.setTimeout(() => {
      setGoogleSessionGraceExpired(true);
    }, 1200);

    return () => window.clearTimeout(timer);
  }, [googleOnboarding, hasSessionUser, authLoading]);

  useEffect(() => {
    if (!isSupabaseMisconfigured) return;
    setError(supabaseConfigErrorMessage);
  }, []);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateMandatorySignupFields({ includeCredentials: true })) return;
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!validateFleetTrialConsent()) return;

    setLoading(true);

    if (isSupabaseMisconfigured) {
      setLoading(false);
      setError(supabaseConfigErrorMessage);
      return;
    }

    if (isMockMode) {
      setTimeout(() => {
        setSuccess(true);
        setLoading(false);
      }, 1000);
      return;
    }

    try {
      const trialMetadata = buildFleetTrialMetadata();
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            full_name: [firstName.trim(), lastName.trim()].filter(Boolean).join(' '),
            company_name: companyName.trim(),
            phone: phone.trim(),
            ...(isFleetTrial ? trialMetadata : {}),
          },
        },
      });

      if (error) throw error;
      if (data.session?.user && isFleetTrial) {
        await initializeFleetTrial(trialMetadata);
        clearPendingFleetTrialMetadata();
        navigate(postAuthPath, { replace: true });
        return;
      }
      if (data.user) setSuccess(true);
    } catch (err: any) {
      console.error(err);
      setError(getErrorMessage(err));
    } finally {
      if (!isMockMode) setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setError(null);

    if (isSupabaseMisconfigured) {
      setError(supabaseConfigErrorMessage);
      return;
    }

    if (!validateMandatorySignupFields()) return;
    if (!validateFleetTrialConsent()) return;

    if (isMockMode) {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        navigate(postAuthPath);
      }, 1000);
      return;
    }

    try {
      persistPendingFleetTrialMetadata(buildFleetTrialMetadata());
      setLoading(true);
      const search = new URLSearchParams();
      search.set('google_onboarding', '1');
      if (onboarding) search.set('onboarding', '1');
      if (postAuthPath !== fallbackPostAuthPath) search.set('next', postAuthPath);
      const redirectTo = `${window.location.origin}/#/signup?${search.toString()}`;
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo },
      });
      if (error) throw error;
    } catch (err: any) {
      console.error(err);
      setLoading(false);
      setError(getErrorMessage(err));
    }
  };

  const handleGoogleCompanySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateMandatorySignupFields()) return;
    if (!validateFleetTrialConsent()) return;
    const cleaned = companyName.trim();

    if (!session?.user) {
      setError('Session missing. Please sign up with Google again.');
      return;
    }

    setLoading(true);
    try {
      const trialMetadata = isFleetTrial ? buildFleetTrialMetadata() : pendingFleetTrialMetadata;
      const { error: metadataError } = await supabase.auth.updateUser({
        data: {
          company_name: cleaned,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          full_name: [firstName.trim(), lastName.trim()].filter(Boolean).join(' '),
          phone: phone.trim(),
          ...(trialMetadata ?? {}),
        },
      });
      if (metadataError) throw metadataError;

      const organisationId = await getCurrentOrganisationId();
      if (!organisationId) {
        throw new Error('Organisation not found. Please try again.');
      }

      const { data: updatedOrganisation, error: orgError } = await supabase
        .from('organisations')
        .update({ name: cleaned, phone: phone.trim() })
        .eq('id', organisationId)
        .select('id')
        .maybeSingle();
      if (orgError) throw orgError;
      if (!updatedOrganisation) {
        throw new Error('Organisation not found. Please try again.');
      }

      if (trialMetadata && isFleetTrialSignup(trialMetadata)) {
        await initializeFleetTrial(trialMetadata);
        clearPendingFleetTrialMetadata();
      }

      navigate(postAuthPath, { replace: true });
    } catch (err: any) {
      console.error(err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleContinueExistingOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!validateMandatorySignupFields()) return;
    if (!validateFleetTrialConsent()) return;

    setLoading(true);
    try {
      const trialMetadata = buildFleetTrialMetadata();
      await supabase.auth.updateUser({ data: trialMetadata });
      await initializeFleetTrial(trialMetadata);
      navigate(postAuthPath, { replace: true });
    } catch (err: any) {
      console.error(err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSeparateWorkspace = async () => {
    setError(null);
    setLoading(true);
    try {
      if (isFleetTrial) persistPendingFleetTrialMetadata(buildFleetTrialMetadata());
      await supabase.auth.signOut();
      setExistingWorkspaceDetailsOpen(false);
      navigate(`/signup${location.search || ''}`, { replace: true });
    } catch (err: any) {
      console.error(err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthShell>
        <div className="w-full max-w-md rounded-3xl border border-white/15 bg-white/95 p-8 text-center shadow-2xl">
          <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Check your email</h2>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            We sent a confirmation link to <strong>{email}</strong>. Confirm your email to activate your workspace.
          </p>
          <Link to={`/login${location.search || ''}`} className={`${PRIMARY_BUTTON_CLASS} mt-7`}>
            Back to Sign In
          </Link>
        </div>
      </AuthShell>
    );
  }

  if (googleOnboarding && !session?.user) {
    if (shouldShowGooglePendingState) {
      return (
        <AuthShell>
          <div className="w-full max-w-md rounded-3xl border border-white/15 bg-white/95 p-8 text-center shadow-2xl">
            <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100">
              <Loader2 className="h-6 w-6 animate-spin text-slate-700" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Finishing Google sign-up</h2>
            <p className="mt-3 text-sm leading-6 text-slate-600">Please wait while we secure your session.</p>
          </div>
        </AuthShell>
      );
    }

    return (
      <AuthShell>
        <div className="w-full max-w-md rounded-3xl border border-white/15 bg-white/95 p-8 text-center shadow-2xl">
          <h2 className="text-2xl font-bold text-slate-900">Google sign-up incomplete</h2>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            We could not find an active Google sign-up session. Please try again.
          </p>
          <Link to="/signup" className={`${PRIMARY_BUTTON_CLASS} mt-7`}>
            Back to Sign Up
          </Link>
        </div>
      </AuthShell>
    );
  }

  if (shouldShowGoogleCompanyStep) {
    return (
      <AuthShell>
        <div className="w-full max-w-md rounded-3xl border border-white/15 bg-white/95 p-8 shadow-2xl">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100">
              <Building2 className="h-6 w-6 text-slate-700" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Complete your sign up</h2>
            <p className="mt-2 text-sm text-slate-600">
              {isFleetTrialFlow ? 'Confirm your trial contact details to finish creating your workspace.' : 'Confirm your workspace details to finish creating your account.'}
            </p>
          </div>

          {error && <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

          <form onSubmit={handleGoogleCompanySubmit} noValidate className="space-y-5">
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 px-4 py-3 text-sm leading-5 text-emerald-950">
              {REQUIRED_DETAILS_NOTE}
            </div>

            <div className={FIELD_GRID_CLASS}>
              <div>
                <label className={LABEL_CLASS}>First Name</label>
                <input
                  type="text"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="John"
                />
              </div>

              <div>
                <label className={LABEL_CLASS}>Last Name</label>
                <input
                  type="text"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="Smith"
                />
              </div>
            </div>

            <div className={FIELD_GRID_CLASS}>
              <div>
                <label className={LABEL_CLASS}>Company Name</label>
                <input
                  type="text"
                  required
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="Acme Fleet Ltd"
                />
              </div>

              <div>
                <label className={LABEL_CLASS}>Phone / WhatsApp</label>
                <input
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="+44 7123 456789"
                />
              </div>
            </div>

            {isFleetTrialFlow && (
              <>
                <label className="flex gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={consentToContact}
                    onChange={(e) => setConsentToContact(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-emerald-500"
                    required
                  />
                  <span>{FLEETMODE_CONSENT_TEXT}</span>
                </label>
              </>
            )}

            <button type="submit" disabled={loading} className={PRIMARY_BUTTON_CLASS}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Continue'
              )}
            </button>
          </form>
        </div>
      </AuthShell>
    );
  }

  if (hasSessionUser && isFleetTrial && !googleOnboarding) {
    return (
      <AuthShell>
        <div className="w-full max-w-lg rounded-3xl border border-white/15 bg-white/95 p-8 shadow-2xl">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100">
              <Building2 className="h-6 w-6 text-slate-700" />
            </div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-emerald-700">Already signed in</p>
            <h2 className="mt-2 text-2xl font-bold text-slate-900">Where should this FleetMode trial start?</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              You are signed in{sessionEmail ? <> as <strong>{sessionEmail}</strong></> : null}. Start the 3-PCN check in this workspace, or create a separate workspace for a new company.
            </p>
          </div>

          {error && <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

          {!existingWorkspaceDetailsOpen ? (
            <div className="space-y-3">
              <button type="button" onClick={() => setExistingWorkspaceDetailsOpen(true)} disabled={loading} className={PRIMARY_BUTTON_CLASS}>
                Start trial in this workspace
              </button>
              <button
                type="button"
                onClick={handleCreateSeparateWorkspace}
                disabled={loading}
                className="flex h-11 w-full items-center justify-center rounded-xl border border-slate-300 bg-white text-sm font-semibold text-slate-800 transition-colors hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Preparing new signup...</> : 'Create a new company workspace'}
              </button>
              <p className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-5 text-amber-950">
                Choose the second option if this browser is still signed in to another company or test workspace.
              </p>
              <a
                href="mailto:support@pcnguard.co.uk?subject=FleetMode%20trial%20workspace"
                className="block pt-1 text-center text-sm font-semibold text-slate-700 hover:text-slate-900"
              >
                Contact support
              </a>
            </div>
          ) : (
            <form onSubmit={handleContinueExistingOrg} noValidate className="space-y-4">
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 px-4 py-3 text-sm leading-5 text-emerald-950">
                Confirm the primary contact for this workspace before the FleetMode trial starts. {REQUIRED_DETAILS_NOTE}
              </div>
              <div className={FIELD_GRID_CLASS}>
                <div>
                  <label className={LABEL_CLASS}>First Name</label>
                  <input type="text" required value={firstName} onChange={(e) => setFirstName(e.target.value)} className={INPUT_CLASS} placeholder="John" />
                </div>
                <div>
                  <label className={LABEL_CLASS}>Last Name</label>
                  <input type="text" required value={lastName} onChange={(e) => setLastName(e.target.value)} className={INPUT_CLASS} placeholder="Smith" />
                </div>
              </div>
              <div className={FIELD_GRID_CLASS}>
                <div>
                  <label className={LABEL_CLASS}>Company Name</label>
                  <input type="text" required value={companyName} onChange={(e) => setCompanyName(e.target.value)} className={INPUT_CLASS} placeholder="Acme Fleet Ltd" />
                </div>
                <div>
                  <label className={LABEL_CLASS}>Phone / WhatsApp</label>
                  <input type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)} className={INPUT_CLASS} placeholder="+44 7123 456789" />
                </div>
              </div>
              <label className="flex gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={consentToContact}
                  onChange={(e) => setConsentToContact(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-emerald-500"
                  required
                />
                <span>{FLEETMODE_CONSENT_TEXT}</span>
              </label>

              <button type="submit" disabled={loading} className={PRIMARY_BUTTON_CLASS}>
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Starting trial...</> : 'Start trial in existing workspace'}
              </button>
              <button
                type="button"
                onClick={() => setExistingWorkspaceDetailsOpen(false)}
                className="block w-full text-center text-sm font-semibold text-slate-700 hover:text-slate-900"
              >
                Back to workspace choices
              </button>
            </form>
          )}
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="w-full max-w-5xl grid items-stretch gap-5 lg:grid-cols-[1.05fr_0.95fr] lg:gap-6">
        <section className="order-2 rounded-3xl border border-white/15 bg-white/10 p-7 text-slate-100 shadow-2xl backdrop-blur-xl lg:order-1">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-white shadow-md overflow-hidden flex items-center justify-center">
              <img
                src={AUTH_LOGO_SRC}
                alt={`${AUTH_BRAND_NAME} logo`}
                className="h-[132%] w-[132%] max-w-none object-contain"
                loading="eager"
                decoding="sync"
              />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-emerald-200">Platform Access</p>
              <h1 className="text-2xl font-bold text-white">{AUTH_BRAND_NAME}</h1>
            </div>
          </div>

          <div className="mt-8">
            <h2 className="text-3xl font-bold leading-tight text-white">Create a professional fleet operations workspace</h2>
            <p className="mt-3 text-sm leading-6 text-slate-200">
              Centralize ticket intake, liability decisions, and driver accountability with an audit-friendly workflow.
            </p>
          </div>

          <div className="mt-8 grid gap-3">
            <div className="rounded-2xl border border-white/15 bg-slate-900/35 px-4 py-3">
              <div className="flex items-center gap-2 text-emerald-200">
                <ShieldCheck className="h-4 w-4" />
                <span className="text-sm font-semibold">Liability Transfer Workflows</span>
              </div>
              <p className="mt-1 text-xs text-slate-200">Reduce payment leakage with structured transfer decisions.</p>
            </div>
            <div className="rounded-2xl border border-white/15 bg-slate-900/35 px-4 py-3">
              <div className="flex items-center gap-2 text-emerald-200">
                <Sparkles className="h-4 w-4" />
                <span className="text-sm font-semibold">AI-Assisted Extraction</span>
              </div>
              <p className="mt-1 text-xs text-slate-200">Capture key ticket data from documents in seconds.</p>
            </div>
            <div className="rounded-2xl border border-white/15 bg-slate-900/35 px-4 py-3">
              <div className="flex items-center gap-2 text-emerald-200">
                <TrendingDown className="h-4 w-4" />
                <span className="text-sm font-semibold">Cost Control Visibility</span>
              </div>
              <p className="mt-1 text-xs text-slate-200">Track trends and recoverability across your fleet estate.</p>
            </div>
          </div>
        </section>

        <section className="order-1 rounded-3xl border border-slate-200 bg-white p-7 shadow-2xl lg:order-2">
          <div className="mb-6 text-center">
            <h3 className="text-2xl font-bold text-slate-900">Join PCNGuard Fleet</h3>
            <p className="mt-2 text-sm text-slate-600">
              {isSupabaseMisconfigured
                ? 'Account creation is temporarily unavailable'
                : isFleetTrial
                  ? 'Start your 7-day 3-PCN AI Control Trial. No card required.'
                  : 'Set up your account after a FleetMode demo or continue an approved trial setup.'}
            </p>
          </div>

          {error && <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

          <form onSubmit={handleSignUp} noValidate className="space-y-4">
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 px-4 py-3 text-sm leading-5 text-emerald-950">
              {REQUIRED_DETAILS_NOTE}
            </div>

            <div className={FIELD_GRID_CLASS}>
              <div>
                <label className={LABEL_CLASS}>First Name</label>
                <input type="text" required value={firstName} onChange={(e) => setFirstName(e.target.value)} className={INPUT_CLASS} placeholder="John" />
              </div>

              <div>
                <label className={LABEL_CLASS}>Last Name</label>
                <input type="text" required value={lastName} onChange={(e) => setLastName(e.target.value)} className={INPUT_CLASS} placeholder="Smith" />
              </div>
            </div>

            <div className={FIELD_GRID_CLASS}>
              <div>
                <label className={LABEL_CLASS}>Company Name</label>
                <input
                  type="text"
                  required
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="Acme Fleet Ltd"
                />
              </div>

              <div>
                <label className={LABEL_CLASS}>Phone / WhatsApp</label>
                <input
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="+44 7123 456789"
                />
              </div>
            </div>

            <div>
              <label className={LABEL_CLASS}>Email Address</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={INPUT_CLASS}
                placeholder="name@company.com"
              />
            </div>

            {isFleetTrial && (
              <label className="flex gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={consentToContact}
                  onChange={(e) => setConsentToContact(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-emerald-500"
                  required
                />
                <span>{FLEETMODE_CONSENT_TEXT}</span>
              </label>
            )}

            <div className={FIELD_GRID_CLASS}>
              <div>
                <label className={LABEL_CLASS}>Password</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="********"
                />
              </div>

              <div>
                <label className={LABEL_CLASS}>Confirm Password</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="********"
                />
              </div>
            </div>

            <button type="submit" disabled={loading || isSupabaseMisconfigured} className={`${PRIMARY_BUTTON_CLASS} mt-2`}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-400">or continue with</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <button
            type="button"
            onClick={handleGoogleSignUp}
            disabled={loading || isSupabaseMisconfigured}
            className="w-full h-11 rounded-xl border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 font-semibold transition-colors flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.26.81-.58z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            Sign up with Google
          </button>

          <div className="mt-6 text-center text-sm text-slate-600">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-slate-900 hover:text-slate-700">
              Sign in
            </Link>
          </div>
        </section>
      </div>
    </AuthShell>
  );
};

export default SignUp;
